// @react
import React, {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useState,
  useContext,
  useEffect,
} from 'react'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @common
import { SnackbarContext } from 'modules/common/context/snackbar'
// @graphql
import { useMutation, useQuery } from '@apollo/client'
import {
  GetPublicUsersQuery,
  GetPublicUsersQueryVariables,
  PublicUsersInput,
  UserFollowUserMutation,
  UserFollowUserMutationVariables,
  UserSearchItem,
  UserUnfollowUserMutation,
  UserUnfollowUserMutationVariables,
} from 'lib/graphql/graphqlTypes'
import client from 'graphql/Apollo/apolloClient'
import { GET_PUBLIC_USERS } from 'graphql/queries/userNetwork.query'
import {
  USER_FOLLOW_USER,
  USER_UNFOLLOW_USER,
} from 'graphql/mutations/userNetwork.mutation'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
// @common
import config from 'constants/config'
import usePagination from 'modules/common/hooks/usePagination'
// @utils
import Helper from 'utils/Helper.class'
// @components
import Grid from '@bit/atd.web.grid'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import FollowCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Follow'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import QueryError from 'modules/common/components/Errors/QueryError'
import PaginationScrollWrapper from 'modules/common/components/PaginationScrollWrapper'

/**
 * 5.0.6.3 Profile - Network - Recommended Users
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3247%3A5776
 *
 *  fields:
 *  user  lastName
 *  user  firstName
 *  user  staff
 *  user  imageUrl
 *  user  jobTitle
 */
const useStyles = makeStyles((theme: Theme) => ({
  cards: {
    '& .MuiGrid-item': {
      [theme.breakpoints.down('sm')]: {
        '@media (min-width: 375px)': {
          flexGrow: 0,
          maxWidth: '50%',
          flexBasis: '50%',
        },
        '@media (min-width: 670px)': {
          flexGrow: 0,
          maxWidth: '33.333%',
          flexBasis: '33.333%',
        },
      },
    },
  },
}))

/******************************************
 * PROPS TYPE
 ******************************************/
type SelfProps = {
  title: string
  subtitle?: string
  actions?: ReactNode
  setCount?: Dispatch<SetStateAction<number>>
  isHiddenPagination?: boolean
  queryInput: PublicUsersInput
  isWidget?: boolean
}
type PropsType = SelfProps

/******************************************
 * COMPONENT
 ******************************************/

const PublicUsersList = ({
  subtitle,
  actions,
  setCount,
  queryInput,
  isHiddenPagination,
  isWidget,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [isSubmitting, setIsMutationSubmitting] = useState<string | null>(null)

  /******************************************
   * HOOKS
   ******************************************/

  const snackbar = useContext(SnackbarContext)
  const classes = useStyles()
  const { Pagination, paginationProps } = usePagination()

  /******************************************
   * GQL HOOKS
   ******************************************/
  const queryVars: GetPublicUsersQueryVariables = {
    input: {
      ...queryInput,
      page: paginationProps.currentPage,
      pageSize: queryInput.pageSize || config.cardsPageSize,
    },
  }
  const { loading: isLoading, error: isError, data } = useQuery<
    GetPublicUsersQuery,
    GetPublicUsersQueryVariables
  >(GET_PUBLIC_USERS, {
    variables: queryVars,
  })

  const [followUser] = useMutation<
    UserFollowUserMutation,
    UserFollowUserMutationVariables
  >(USER_FOLLOW_USER)

  const [unfollowUser] = useMutation<
    UserUnfollowUserMutation,
    UserUnfollowUserMutationVariables
  >(USER_UNFOLLOW_USER)

  useEffect(() => {
    setCount && setCount(data?.publicUsers?.meta?.count || 0)
  }, [setCount, data])

  /******************************************
   * FUNCTIONS
   ******************************************/

  /**
   *
   * @param item
   */
  const followUserAction = async (item: UserSearchItem) => {
    setIsMutationSubmitting(item.id)
    const { errors: mutationErrors } = await followUser({
      variables: {
        input: {
          userId: item.id,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: `Successfully followed ${item.firstName}`,
        show: true,
      })

      client.cache.modify({
        id: client.cache.identify(item),
        fields: {
          isFollowedByCurrentUser() {
            return true
          },
        },
      })
      // await refetch()
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: `There was a problem following ${item.firstName}`,
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /**
   *
   * @param item
   */
  const unfollowUserAction = async (item: UserSearchItem) => {
    setIsMutationSubmitting(item.id)
    const { errors: mutationErrors } = await unfollowUser({
      variables: {
        input: {
          userId: item.id,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: `Successfully unfollowed ${item.firstName}`,
        show: true,
      })

      client.cache.modify({
        id: client.cache.identify(item),
        fields: {
          isFollowedByCurrentUser() {
            return false
          },
        },
      })
      // await refetch()
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: `There was a problem unfollowing ${item.firstName}`,
        show: true,
      })
    }
    setIsMutationSubmitting(null)
  }

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const skelContent = (
    <Grid className={classes.cards} container spacing={3}>
      {Array(12)
        .fill('')
        .map((item, index) => {
          return (
            <Grid key={`ske_loader_${index}`} xs={12} sm={3} item>
              <SkeletonLoader />
            </Grid>
          )
        })}
    </Grid>
  )

  const usersList =
    data?.publicUsers?.data && data?.publicUsers?.data.length ? (
      <PaginationScrollWrapper {...paginationProps}>
        <Grid className={classes.cards} container spacing={3}>
          {data?.publicUsers?.data.map((item) => {
            const followCTAText = item?.isFollowedByCurrentUser
              ? 'Unfollow'
              : 'Follow'
            const followCTACallback = item?.isFollowedByCurrentUser
              ? unfollowUserAction
              : followUserAction

            return (
              <Grid key={`${item.id}`} xs={12} sm={isWidget ? 12 : 3} item>
                <FollowCard
                  titleURL={item.url}
                  staff={item?.staff}
                  titleTargetUrl="_blank"
                  avatar={{
                    size: 48,
                    src: item?.imageUrl as string,
                    alt: `${item?.firstName} ${item?.lastName}`,
                    bgColor: Helper.randomColorChooser(),
                    textAs: (
                      <p>
                        {Helper.getAcronymFromSentence(
                          `${item?.firstName} ${item?.lastName}`,
                        )}
                      </p>
                    ),
                  }}
                  layout={isWidget === true ? 'horizontal' : 'auto'}
                  title={`${item?.firstName} ${item?.lastName}`}
                  subtitle={item?.jobTitle || ''}
                  btnLabel={followCTAText}
                  following={item?.isFollowedByCurrentUser}
                  isLoading={isSubmitting === item.id}
                  onButtonClick={() =>
                    followCTACallback(item as UserSearchItem)
                  }
                />
              </Grid>
            )
          })}
        </Grid>
        {isHiddenPagination === true ? (
          <></>
        ) : (
          <PaginationWrapper>
            <Pagination
              {...paginationProps}
              count={data.publicUsers.meta?.totalPages}
            />
          </PaginationWrapper>
        )}
      </PaginationScrollWrapper>
    ) : (
      <div>There are no results!</div>
    )

  return (
    <>
      {subtitle && (
        <Grid container style={{ marginBottom: '32px' }}>
          <Grid item xs={12}>
            <Typography>{subtitle}</Typography>
          </Grid>
        </Grid>
      )}

      {actions && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {actions}
          </Grid>
        </Grid>
      )}

      {isLoading ? skelContent : usersList}
    </>
  )
}

export default PublicUsersList
